// pages/TaskManagement/CurrentDeptActivity.js
//
// Legacy: TasksSpotCheck — "what is my team doing RIGHT NOW".
// Round TM-7 (2026) — audit Deliverable 8: one row per open time-log of a
// DIRECT report, an IDLE row for anyone with no open session, Personal
// rows masked server-side (and not clickable). Auto-refreshes every 60s
// like the legacy timer.
// Backend: GET /api/TaskManagement/current-dept-activity

import React, { useCallback, useEffect, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { DashboardPageHeader, EmptyState, TableSkeleton } from "../../Components/Common/DashboardKit";
import api from "../../api";

const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-");
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");

const CurrentDeptActivity = () => {
    document.title = "Current Department Activity || SSW Technologies Dashboard";
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [asOf, setAsOf] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async (quiet) => {
        try {
            if (!quiet) setLoading(true);
            setError("");
            const res = await api.get("/TaskManagement/current-dept-activity");
            const data = res?.data ?? res;
            setRows(Array.isArray(data?.items) ? data.items : []);
            setAsOf(data?.asOf || null);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message
                || "Unable to load department activity.");
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(false);
        const timer = setInterval(() => load(true), 60000);
        return () => clearInterval(timer);
    }, [load]);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-pulse-line"
                    title="Current Department Activity"
                    subtitle="What your direct reports are working on right now"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Current Activity" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <span className="text-muted">
                                {asOf ? `As of ${new Date(asOf).toLocaleTimeString()}` : ""}
                                {"  "}· refreshes every 60s
                            </span>
                            <Button size="sm" color="light" onClick={() => load(false)}>
                                <i className="ri-refresh-line align-bottom"></i>
                            </Button>
                        </div>

                        {loading ? (
                            <TableSkeleton rows={6} cols={6} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-team-line"
                                title="No direct reports"
                                message="Nobody reports to you in the employee tree."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Employee</th>
                                            <th>Working On</th>
                                            <th>Type</th>
                                            <th>Started</th>
                                            <th>Due</th>
                                            <th>Assigned By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr
                                                key={`${r.empId}-${r.taskId ?? "idle"}-${i}`}
                                                role={r.taskId && !r.isPersonal ? "button" : undefined}
                                                onClick={() => r.taskId && !r.isPersonal
                                                    && navigate(`/task-management/view-task/${r.taskId}`)}
                                                title={r.isPersonal ? "Personal task" : r.taskId ? `Open Task #${r.taskId}` : undefined}
                                            >
                                                <td className="fw-semibold">{r.name}</td>
                                                <td className="text-truncate" style={{ maxWidth: "420px" }}>
                                                    {r.idle ? (
                                                        <Badge color="secondary">IDLE</Badge>
                                                    ) : r.isPersonal ? (
                                                        <span className="text-muted fst-italic">{r.description}</span>
                                                    ) : (
                                                        <>
                                                            <span className="text-muted me-1">#{r.taskId}</span>
                                                            {r.description}
                                                        </>
                                                    )}
                                                </td>
                                                <td>{r.taskType || "-"}</td>
                                                <td>{r.idle ? "-" : fmtTime(r.startedOn)}</td>
                                                <td>{r.idle ? "-" : fmtDate(r.dueDate)}</td>
                                                <td>{r.assignedByName || "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default CurrentDeptActivity;
